import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import receiptPdf from './assets/sport_processing_receipt_2025-03-12.pdf'
import manifestLabelPdf from './assets/manifest-label-template_2024-05-13.pdf'
import { DateTime } from "luxon";
import { formatPhone, billAddress, shipAddress, lastNameCommaFirstName, trimSubString, orderDetailToWeighInDetail, formatContactMethod } from './helpers.js'
import JsBarcode from "jsbarcode"
import { useState } from 'react';

async function tryEmbedPng(pdfDoc, blob, meta = {}) {
  if (!blob) {
    return null
  }
  if (!(blob instanceof Blob) || blob.size <= 0) {
    console.log("Skipping signature embed; empty or invalid blob.", {
      orderId: meta.orderId ?? null,
      size: blob?.size ?? null,
      type: blob?.type ?? null,
    })
    return null
  }
  try {
    const ab = await blob.arrayBuffer()
    return await pdfDoc.embedPng(ab)
  } catch (e) {
    console.log("Skipping signature embed; PNG embed failed.", {
      orderId: meta.orderId ?? null,
      size: blob?.size ?? null,
      type: blob?.type ?? null,
      error: e,
    })
    return null
  }
}

export async function showManifestLabelPdf(manifest, location) {
  const existingPdfBytes = await fetch(manifestLabelPdf).then(res => res.arrayBuffer())
  const pdfDoc = await PDFDocument.load(existingPdfBytes)
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const pages = pdfDoc.getPages()
  const firstPage = pages[0]
  const { height } = firstPage.getSize()

  let textColor = rgb(0, 0, 0)

  const date = manifest.received_at ? DateTime.fromISO(manifest.received_at) : DateTime.fromISO(manifest.shipped_at)
  const dateStr = date.toFormat('LLL dd / yy')

  firstPage.drawText(dateStr, {
    x: 37,
    y: height - 172,
    size: 24,
    font: helveticaFont,
    color: textColor,
  })

  firstPage.drawText("Sport", {
    x: 37,
    y: height - 256,
    size: 24,
    font: helveticaFont,
    color: textColor,
  })

  firstPage.drawText(location.short_code, {
    x: 37,
    y: height - 340,
    size: 24,
    font: helveticaFont,
    color: textColor,
  })

  firstPage.drawText(manifest.label_description ? manifest.label_description : "", {
    x: 37,
    y: height - 424,
    size: 20,
    font: helveticaFont,
    color: textColor,
  })

  firstPage.drawText(manifest.manifest_num, {
    x: 250,
    y: height - 300,
    size: 120,
    font: helveticaFont,
    color: textColor,
  })

  const pdfBytes = await pdfDoc.save()
  var file = new Blob([pdfBytes], { type: 'application/pdf' });
  var fileURL = URL.createObjectURL(file);
  window.open(fileURL);
}

export async function generateReceiptPdfPage(page, helveticaFont, helveticaFontBold, order, master, locations, signaturePdfImage, species, options, weighInProcesses) {
  const { height } = page.getSize()

  let textColor = rgb(0, 0, 0)
  let textNegative = rgb(1, 1, 1)

  page.drawText(locations.find(location => location.id === order.location_id).name ?? "", {
    x: 190,
    y: height - 43,
    size: 11,
    font: helveticaFont,
    color: textColor,
  })

  if (order.ordered_at) {
    const date = new Date(order.ordered_at)
    const month = ((date.getUTCMonth() + 1) + "").padStart(2, '0')
    const day = (date.getUTCDate() + "").padStart(2, '0')
    const year = date.getUTCFullYear() + ""

    page.drawText(`${year}-${month}-${day}`, {
      x: 190,
      y: height - 29,
      size: 11,
      font: helveticaFont,
      color: textColor,
    })
  }

  if (order.trip_code) {
    page.drawText(`TRIP:`, {
      x: 255,
      y: height - 29,
      size: 11,
      font: helveticaFontBold,
      color: textColor,
    })

    page.drawText(order.trip_code ?? "", {
      x: 285,
      y: height - 29,
      size: 11,
      font: helveticaFont,
      color: textColor,
    })
  }

  page.drawText(`STAFF:`, {
    x: 325,
    y: height - 29,
    size: 11,
    font: helveticaFontBold,
    color: textColor,
  })

  page.drawRectangle({
    x: 370,
    y: height - 31,
    width: 12,
    height: 12,
    borderColor: rgb(0, 0, 0),
    borderWidth: 1,
  })

  if (order.is_staff) {
    page.drawText(`X`, {
      x: 372,
      y: height - 30,
      size: 13,
      font: helveticaFontBold,
      color: textColor,
    })
  }

  let masterX = 450
  if (master.length > 7) {
    masterX = 405
  }

  page.drawText(master ?? "", {
    x: masterX,
    y: height - 32,
    size: 28,
    font: helveticaFontBold,
    color: textColor,
  })

  // Print CODE128 barcode of master #
  if (master) {
    const data = {}
    JsBarcode(data, master)
    let x = masterX
    let y = height - 60
    for (var i = 0; i < data.encodings[0].data.length; i++) {
      if (data.encodings[0].data[i] === "1") {
        page.drawRectangle({
          x: x,
          y: y,
          width: 1.3,
          height: 22,
          color: rgb(0, 0, 0),
        })
      }
      x = x + 1.3
    }
  }

  const billTo = billAddress(order)
  page.drawText(lastNameCommaFirstName(billTo).toUpperCase(), {
    x: 40,
    y: height - 82,
    size: 14,
    font: helveticaFontBold,
    color: textNegative,
  })

  page.drawText(billTo.street_address ? billTo.street_address.toUpperCase() : "", {
    x: 55,
    y: height - 105,
    size: 11,
    font: helveticaFont,
    color: textColor,
  })

  var billToLine2 = trimSubString([billTo.city ?? "", billTo.state ?? ""].join(", "), ", ")
  page.drawText(billToLine2.toUpperCase(), {
    x: 55,
    y: height - 119,
    size: 11,
    font: helveticaFont,
    color: textColor,
  })

  var billToLine3 = trimSubString([billTo.country ?? "", billTo.zip ?? ""].join(", "), ", ")
  page.drawText(billToLine3.toUpperCase(), {
    x: 55,
    y: height - 133,
    size: 11,
    font: helveticaFont,
    color: textColor,
  })

  page.drawText("Fishing License: ", {
    x: 55,
    y: height - 149,
    size: 10,
    font: helveticaFont,
    color: textColor,
  })

  page.drawText(order.fishing_license ?? "", {
    x: 130,
    y: height - 149,
    size: 10,
    font: helveticaFont,
    color: textColor,
  })

  page.drawText(billTo.phone ? formatPhone(billTo.phone) : "", {
    x: 287,
    y: height - 105,
    size: 11,
    font: helveticaFont,
    color: textColor,
  })

  if (billTo.email) {
    let billEmailFontSize = 7 // default for really long emails
    if (billTo.email.length < 20) {
      billEmailFontSize = 10
    } else if (billTo.email.length < 25) {
      billEmailFontSize = 9
    } else if (billTo.email.length < 30) {
      billEmailFontSize = 8
    }
    page.drawText(billTo.email.toUpperCase(), {
      x: 287,
      y: height - 119,
      size: billEmailFontSize,
      font: helveticaFont,
      color: textColor,
    })
  }

  page.drawText("Contact: ", {
    // Right column, in line with fishing license
    x: 287,
    y: height - 149,
    size: 10,
    font: helveticaFont,
    color: textColor,
  })

  page.drawText(formatContactMethod(order.contact_method), {
    x: 328,
    y: height - 149,
    size: 10,
    font: helveticaFont,
    color: textColor,
  })

  if (!order.bill_same_as_ship) {
    const shipTo = shipAddress(order)
    let shipName = trimSubString([shipTo?.last_name ?? "", shipTo?.first_name ?? ""].join(", "), ", ")
    let shipStreet = shipTo?.street_address
    let shipCity = shipTo?.city
    let shipState = shipTo?.state
    let shipPhone = formatPhone(shipTo?.phone ?? "")
    let shipZip = shipTo?.zip
    let shipCountry = shipTo?.country
    let shipEmail = shipTo?.email
    let location = locations.find(location => location.id === order.pick_up_location_id)
    if (order.pick_up_location_id) {
      shipName = "PICK UP IN STORE"
      shipStreet = location.pick_up_name || location.name
      shipCity = ""
      shipState = ""
      shipPhone = ""
      shipZip = ""
      shipCountry = ""
      shipEmail = ""
    }

    const shipHeightOffset = 97

    page.drawText(shipName.toUpperCase(), {
      x: 40,
      y: height - 82 - shipHeightOffset,
      size: 14,
      font: helveticaFontBold,
      color: textColor,
    })

    page.drawText(shipStreet ? shipStreet.toUpperCase() : "", {
      x: 55,
      y: height - 105 - shipHeightOffset,
      size: 11,
      font: helveticaFont,
      color: textColor,
    })

    var shipToLine2 = trimSubString([shipCity ?? "", shipState ?? ""].join(", "), ", ")
    page.drawText(shipToLine2.toUpperCase(), {
      x: 55,
      y: height - 119 - shipHeightOffset,
      size: 11,
      font: helveticaFont,
      color: textColor,
    })

    var shipToLine3 = trimSubString([shipCountry ?? "", shipZip ?? ""].join(", "), ", ")
    page.drawText(shipToLine3.toUpperCase(), {
      x: 55,
      y: height - 133 - shipHeightOffset,
      size: 11,
      font: helveticaFont,
      color: textColor,
    })

    page.drawText(shipPhone ? formatPhone(shipPhone) : "", {
      x: 287,
      y: height - 105 - shipHeightOffset,
      size: 11,
      font: helveticaFont,
      color: textColor,
    })

    if (shipEmail) {
      let shipEmailFontSize = 7 // default for really long emails
      if (shipEmail.length < 20) {
        shipEmailFontSize = 10
      } else if (shipEmail.length < 25) {
        shipEmailFontSize = 9
      } else if (shipEmail.length < 30) {
        shipEmailFontSize = 8
      }
      page.drawText(shipEmail.toUpperCase(), {
        x: 287,
        y: height - 119 - shipHeightOffset,
        size: shipEmailFontSize,
        font: helveticaFont,
        color: textColor,
      })
    }
  }

  page.drawText(`FISH TAG:`, {
    x: 27,
    y: height - 259,
    size: 12,
    font: helveticaFontBold,
    color: textColor,
  })

  page.drawText(`${order.fish_tag ? order.fish_tag : ""}`, {
    x: 95,
    y: height - 259,
    size: 12,
    font: helveticaFont,
    color: textColor,
  })

  if (!order.hold_for_pick_up) {
    const enrichedDetails = order.details.map(detail => {
      const enrichedDetail = orderDetailToWeighInDetail(detail, order.options, options, weighInProcesses)
      enrichedDetail.species = trimSubString(trimSubString(species.find(x => x.id === enrichedDetail.species_id)?.name ?? "(Unknown Species)", "Salmon"), "Cod").trim()

      enrichedDetail.weigh_in_process_name = weighInProcesses.find(x => x.id === enrichedDetail.weigh_in_process_id)?.oms_name ?? "(Unknown Process)"
      enrichedDetail.fish_description = detail.fish_description?.trim()
      return enrichedDetail
    })

    const heightOffset = 282
    const rowHeight = 44
    enrichedDetails.forEach((detail, i) => {
      page.drawText(`x${detail.original_num_fish}`, {
        x: 40,
        y: height - heightOffset - (i * rowHeight),
        size: 14,
        font: helveticaFont,
        color: textColor,
      })

      page.drawText(`${detail.species}`, {
        x: 75,
        y: height - heightOffset - (i * rowHeight),
        size: 14,
        font: helveticaFont,
        color: textColor,
      })

      page.drawText(`${detail.weigh_in_process_name}`, {
        x: 170,
        y: height - heightOffset - (i * rowHeight),
        size: 14,
        font: helveticaFont,
        color: textColor,
      })

      page.drawText(`${detail.fish_description ?? ""}`, {
        x: 40,
        y: height - (heightOffset + 15) - (i * rowHeight),
        size: 12,
        font: helveticaFont,
        color: textColor,
      })
    })
  } else {
    page.drawText(`HOLD FOR PICKUP`, {
      x: 40,
      y: height - 287 - (i * 29),
      size: 14,
      font: helveticaFont,
      color: textColor,
    })
  }

  page.drawText(order.total_weight_lb ? order.total_weight_lb + " LBS" : "", {
    x: 520,
    y: height - 189,
    size: 10,
    font: helveticaFont,
    color: textColor,
  })

  page.drawText(order.total_boxes + "", {
    x: 520,
    y: height - 217,
    size: 10,
    font: helveticaFont,
    color: textColor,
  })

  if (signaturePdfImage) {
    const imageDims = signaturePdfImage.scale(0.35)
    page.drawImage(signaturePdfImage, {
      x: 70,
      y: height - 748,
      width: imageDims.width,
      height: imageDims.height
    })
  }
}

export async function generateBatchReceiptPdf(orderConfigs, locations, species, options, weighInProcesses) {
  const pdfDoc = await PDFDocument.create()
  const existingPdfBytes = await fetch(receiptPdf).then(res => res.arrayBuffer())
  const preloadedReceiptDoc = await PDFDocument.load(existingPdfBytes)
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

  for (let i = 0; i < orderConfigs.length; i++) {
    const pages = await pdfDoc.copyPages(preloadedReceiptDoc, [0])
    const preLoadedPage = pages[0]
    pdfDoc.addPage(preLoadedPage)

    const pdfImage = await tryEmbedPng(pdfDoc, orderConfigs[i].signatureBlob, {
      orderId: orderConfigs[i].order?.id,
    })
    generateReceiptPdfPage(preLoadedPage, helveticaFont, helveticaFontBold, orderConfigs[i].order, orderConfigs[i].master, locations, pdfImage, species, options, weighInProcesses)
  }

  return await pdfDoc.save()
}

export async function generateReceiptPdf(order, master, locations, signatureBlob, species, options, weighInProcesses) {
  const existingPdfBytes = await fetch(receiptPdf).then(res => res.arrayBuffer())
  const pdfDoc = await PDFDocument.load(existingPdfBytes)
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
  const pages = pdfDoc.getPages()
  const firstPage = pages[0]

  const pdfImage = await tryEmbedPng(pdfDoc, signatureBlob, { orderId: order?.id })
  generateReceiptPdfPage(firstPage, helveticaFont, helveticaFontBold, order, master, locations, pdfImage, species, options, weighInProcesses)

  return await pdfDoc.save()
}

export async function showReceiptPdf(order, master, locations, signatureBlob, species, options, weighInProcesses) {
  const pdfBytes = await generateReceiptPdf(order, master, locations, signatureBlob, species, options, weighInProcesses)
  const file = new Blob([pdfBytes], { type: 'application/pdf' });
  var fileURL = URL.createObjectURL(file);
  window.open(fileURL);
}

/**
 * Uses state whose value remains normalized
 * @template T The type of the state
 * @param {T} initialValue The initial value the state should have. This value will be normalized.
 * @param {function(T): T} normalize A function that normalizes any new value
 * @returns {[T, function(React.SetStateAction<T>): void]} An array containing the state variable followed by a setter
 */
export function useNormalizedState(initialValue, normalize) {
  const [value, setValue] = useState(initialValue)

  function setter(arg) {
    if (typeof arg === "function") {
      setValue(prev => normalize(arg(prev)))
    } else {
      setValue(normalize(arg))
    }
  }
  return [value, setter]
}

/**
 * Normalizes a customer's name according to business requirements
 * @param {?string | undefined} name
 * @returns {string | undefined}
 */
export function normalizeCustomerName(name) {
  // They want commas removed
  return name?.replaceAll(",", "")
}